import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { useIsAdmin } from "../hooks/useFeatureFlags";
import { friendlyMessageForApiError } from "../api/errorUtils";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { getDatabases, setDatabaseClosed, deleteDatabase } from "../api/adminApi";
import DeleteDatabaseModal from "../components/indexes/DeleteDatabaseModal";
import { Hero } from "../components/adminSurface/AdminSurface";
import { StackArt } from "../components/adminSurface/heroArt";

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="arch-empty">
      <div className="arch-empty-icon">📦</div>
      <div className="arch-empty-title">
        {t("admin.noArchived", { defaultValue: "No archived databases" })}
      </div>
      <div className="arch-empty-desc">
        {t("admin.noArchivedDesc", {
          defaultValue:
            "Databases you archive from the Indexes page will appear here and can be restored at any time.",
        })}
      </div>
    </div>
  );
}

export default function ArchivedDatabases() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const isAdmin = useIsAdmin();

  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalTarget, setModalTarget] = useState(null);

  function getDbId(db) {
    return db.id ?? db.Id ?? db.name ?? db.Name ?? "";
  }
  function getDbLabel(db) {
    return db.displayName || db.DisplayName || db.name || db.Name || getDbId(db);
  }

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const all = await getDatabases(token);
        setDatabases((all || []).filter((d) => d.isClosed || d.IsClosed));
      } catch (e) {
        setError(friendlyMessageForApiError(e));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token]);

  useEffect(() => {
    if (success) {
      const id = setTimeout(() => setSuccess(""), 4000);
      return () => clearTimeout(id);
    }
  }, [success]);

  async function handleConfirmArchive() {
    if (!modalTarget) return;
    const { id: dbId, label: dbLabel } = modalTarget;
    setModalTarget(null);
    setLoading(true);
    try {
      await setDatabaseClosed(token, dbId, false);
      const all = await getDatabases(token);
      setDatabases((all || []).filter((d) => d.isClosed || d.IsClosed));
      setSuccess(t("admin.databaseUnarchived", { dbName: dbLabel, defaultValue: `"${dbLabel}" restored successfully.` }));
    } catch (e) {
      setError(friendlyMessageForApiError(e));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmDelete() {
    if (!modalTarget) return;
    const { id: dbId, label: dbLabel } = modalTarget;
    setModalTarget(null);
    setLoading(true);
    try {
      await deleteDatabase(token, dbId);
      const all = await getDatabases(token);
      setDatabases((all || []).filter((d) => d.isClosed || d.IsClosed));
      setSuccess(t("admin.databaseDeleted", { dbName: dbLabel, defaultValue: `"${dbLabel}" permanently deleted.` }));
    } catch (e) {
      setError(friendlyMessageForApiError(e));
    } finally {
      setLoading(false);
    }
  }

  const archived = databases;

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <HeaderBar />

      <div className="page-container">
        <div className="arch-page">

          <Hero
            title={t("admin.archivedDatabases", { defaultValue: "Archived Databases" })}
            subtitle={t("admin.archivedSubtitle", {
              defaultValue: "Restore or permanently remove soft-deleted databases.",
            })}
            logo={<StackArt />}
            statValue={loading ? "…" : archived.length}
            statLabel={t("admin.archivedStatLabel", { defaultValue: "Archived" })}
            statDotColor="#f59e0b"
          />

          {error && (
            <div className="arch-alert arch-alert--error" style={{ marginBottom: 12 }}>{error}</div>
          )}
          {success && (
            <div className="arch-alert arch-alert--success" style={{ marginBottom: 12 }}>{success}</div>
          )}

          <div className="arch-card">
            {loading && archived.length === 0 ? (
              <div className="arch-loading">
                {t("admin.loading", { defaultValue: "Loading…" })}
              </div>
            ) : archived.length === 0 ? (
              <EmptyState />
            ) : (
              <table className="arch-table">
                <thead>
                  <tr>
                    <th>{t("admin.databaseName", { defaultValue: "Database" })}</th>
                    <th>{t("admin.organization", { defaultValue: "Organization" })}</th>
                    <th>{t("admin.fileType", { defaultValue: "File type" })}</th>
                    <th style={{ textAlign: "right" }}>
                      {t("admin.actions", { defaultValue: "Actions" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {archived.map((db) => {
                    const id = getDbId(db);
                    const label = getDbLabel(db);
                    const org = db.organization || db.Organization || "—";
                    const fileType =
                      db.fileType?.typeName ||
                      db.FileType?.typeName ||
                      db.fileTypeId ||
                      db.FileTypeId ||
                      "—";
                    return (
                      <tr key={id}>
                        <td>
                          <div className="arch-db-name">{label}</div>
                          {label !== (db.name || db.Name) && (
                            <div className="arch-db-id">{db.name || db.Name}</div>
                          )}
                        </td>
                        <td className="arch-cell-muted">{org}</td>
                        <td className="arch-cell-muted">{fileType}</td>
                        <td>
                          <div className="arch-actions">
                            <button
                              type="button"
                              className="arch-btn arch-btn--restore"
                              disabled={!isAdmin || loading}
                              onClick={() => setModalTarget({ id, label, isClosed: true })}
                            >
                              ↩ {t("admin.restore", { defaultValue: "Restore" })}
                            </button>
                            <button
                              type="button"
                              className="arch-btn arch-btn--delete"
                              disabled={!isAdmin || loading}
                              onClick={() => setModalTarget({ id, label, isClosed: true })}
                            >
                              {t("admin.deletePermanently", { defaultValue: "Delete permanently" })}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

        </div>
      </div>

      <DeleteDatabaseModal
        target={modalTarget}
        onArchive={handleConfirmArchive}
        onDelete={handleConfirmDelete}
        onCancel={() => setModalTarget(null)}
      />
    </div>
  );
}
